<template>
    <div class="row">
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Assigned To</span>
                    <!-- If we have a filtered list of technicians, show a select dropdown -->
                    <select 
                        v-if="filteredTechnicians.length > 0"
                        class="form-select form-select-sm"
                        v-model="form.AssignedTo"
                    >
                        <option value="">Select technician...</option>
                        <option 
                            v-for="tech in filteredTechnicians" 
                            :key="tech.Id" 
                            :value="tech.Id"
                        >
                            {{ tech.TechnicianName || tech.Name }}
                        </option>
                    </select>
                    <!-- Otherwise, show the full tech autocomplete -->
                    <tech-ac 
                        v-else
                        v-model="form.AssignedTo" 
                        placeholder="Search for a technician..."
                    ></tech-ac>
                </label>
                <small v-if="filteredTechnicians.length > 0" class="text-muted">
                    Showing assigned technicians only
                </small>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Client</span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm" 
                        placeholder="Search for a client..."
                    ></client-ac>
                </label>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Request/Job</span>
                    <job-ac 
                        v-model="form.RequestId" 
                        :client="form.CustomerNumber"
                        placeholder="Search for a request..."
                    ></job-ac>
                </label>
            </div>
        </div>
    </div>

    <div class="row">
        <div class="col-md-6">
            <label class="d-block mb-3">
                <span>Estimated Time</span>
                <input 
                    class="form-control form-control-sm" 
                    type="text" 
                    v-model="form.EstimatedTime" 
                    placeholder="Hour Estimate" 
                />
            </label>
        </div>
        <div class="col-md-6">
            <label class="d-block mb-3">
                <span>Complete By</span>
                <input type="date" 
                    class="form-control form-control-sm" 
                    v-model="form.CompleteByDate" 
                    placeholder="Complete By"
                >
            </label>
        </div>  
    </div>

    <div class="row">
        <div class="col-12">
            <label class="d-block mb-3">
                <span>Work Assignment</span>
                <textarea class="form-control form-control-sm" placeholder="Work Assignment" v-model="form.WorkAssignment"
                    rows="6"></textarea>
            </label>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from "vue";
import TechAc from "@/components/Techs/TechAc.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import ApiService from "@/services/ApiService";
import { useTechStore } from "@/stores/TechStore";
import { storeToRefs } from "pinia";

const props = defineProps({
    assignedTechnicianIds: {
        type: Array as () => number[],
        default: () => []
    },
    ownerId: {
        type: Number,
        default: null
    }
});

const model = defineModel<any>();

const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

// Build filtered list of technicians from assigned IDs and owner
const filteredTechnicians = computed(() => {
    // Combine owner and assigned technician IDs
    const techIds = new Set<number>();
    
    if (props.ownerId) {
        techIds.add(props.ownerId);
    }
    
    if (props.assignedTechnicianIds && props.assignedTechnicianIds.length > 0) {
        props.assignedTechnicianIds.forEach(id => techIds.add(id));
    }
    
    // If no specific technicians, return empty (will show full autocomplete)
    if (techIds.size === 0) {
        return [];
    }
    
    // Filter TechList to only include assigned technicians
    return Object.values(TechList.value).filter((tech: any) => techIds.has(tech.Id));
});

const form = computed({
    get: () => {
        if (model.value && model.value.Type !== 'assignment') {
            model.value.Type = 'assignment';
        }
        return model.value || {};
    },
    set: (value) => {
        if (value) {
            value.Type = 'assignment';
        }
        model.value = value;
    }
});
</script>
